import { TIMEOUT_NONE } from '@/api/timeouts'
import type { ApiResponse } from '@/stores/auth'
import { isDemoDeployment } from '@/utils/deployment'
import { apiCall } from './axiosInstance'
import { dbcApi as demoDbcApi } from './demo/dbcApi'

export interface DBCAdditionalSettings {
	add_suffix_canid?: boolean
	dont_decode_choices?: boolean
	lower_threshold?: number | null
	upper_threshold?: number | null
	dbc_ver?: number | null
}

export interface DBCData {
	dbc_type: DBCTypes
	name?: string
	version?: string
	input_table?: string
	output_table?: string
	period: number
	obd_can_id: number | null
	j1939_pgns: number[] | null
	additional_settings?: DBCAdditionalSettings
}
export type EditDBCParserInput = {
	name: string
	period: number | string
	version: string
	additional_settings: DBCAdditionalSettings
}
interface DBCSignals {
	byte_order: string
	choices: Record<string, unknown> | null
	is_signed: boolean
	is_float: boolean
	length: number
	name: string
	offset: number
	scale: number
	start: number
}

export interface DBCMessageResponse {
	can_id: number
	name: string
	signals: DBCSignals[]
}
export interface DBCJSON {
	messages: DBCMessageResponse[]
}

export type DBCJSONResponse = ApiResponse<DBCJSON, 'json'>
export type ParsedDBCResponse = ApiResponse<{ json: DBCJSON }, 'results'>

export type DBCFile = {
	dbc?: string
}
export type DBCFileResponse = ApiResponse<DBCFile, 'results'>
export type SingleDBCResponse = ApiResponse<DBCResponse, 'results'>
export type DBCActionMessageResponse = {
	message: string
}
export interface DBCOffsetResponse {
	[key: string]: number
}

export type DBCTypes = 'custom' | 'obd' | 'j1939'
export type DBCStatus = 'updating' | 'started' | 'stopped'
export interface DBCResponse {
	id: string
	dbc_type: DBCTypes
	name: string
	tenant_id: string
	version: string
	input_table: string
	output_table: string
	status: DBCStatus
	period: number
	offsets: DBCOffsetResponse
	additional_settings: DBCAdditionalSettings | null
	obd_can_id: number | null
	j1939_pgns: number[] | null
	last_error: string | null
	last_error_timestamp: string | null
	max_input_stream_timestamp?: number | null
}

export interface DbcFilter {
	name?: string[]
	dbc_type?: DBCTypes[]
	input_table?: string[]
	output_table?: string[]
	status?: DBCStatus
	last_processed_after?: string
}

export interface PaginatedDBCResponse {
	count: number
	data: DBCResponse[]
	summary?: {
		obd?: number
		custom?: number
		j1939?: number
	}
}

export type DBCSignalWithCAN = {
	can_id: number
	byte_order: string
	choices: Record<string, unknown> | null
	database_column: string
	is_signed: boolean
	is_float: boolean
	length: number
	name: string
	offset: number
	scale: number
	start: number
}

export type DBCDiffResponse = {
	deleted_signals: DBCSignalWithCAN[]
	added_signals: DBCSignalWithCAN[]
	updated_signals: { from: DBCSignalWithCAN; to: DBCSignalWithCAN }[]
}

export type UpdatedSignal = {
	from: DBCSignalWithCAN
	to: DBCSignalWithCAN
}

// Batch operation types
export type BatchDBCRequest = {
	dbc_ids: string[]
}
export type BatchActionResponse = ApiResponse<string, 'results'>

const v3DbcApi = {
	getDBCLists: async (
		params?: { page?: number; per_page?: number },
		filters?: DbcFilter,
	): Promise<PaginatedDBCResponse> => {
		const queryParams = new URLSearchParams()
		if (params?.page !== undefined) {
			queryParams.set('page', String(params.page))
		}
		if (params?.per_page !== undefined) {
			queryParams.set('per_page', String(params.per_page))
		}
		const queryString = queryParams.toString()
		return apiCall(`/v3/dbcs/dashboard${queryString ? `?${queryString}` : ''}`, {
			method: 'POST',
			data: filters ?? {},
		})
	},
	getDBC: async (dbcID: string): Promise<SingleDBCResponse> => apiCall(`/v3/dbcs/${encodeURIComponent(dbcID)}`),
	getDBCFile: async (dbcID: string): Promise<DBCFileResponse> => apiCall(`/v3/dbcs/${encodeURIComponent(dbcID)}/dbc`),
	getParsedDBC: async (dbcID: string): Promise<ParsedDBCResponse> =>
		apiCall(`/v3/dbcs/${encodeURIComponent(dbcID)}/json`),
	getDBCDiff: async (dbcID: string, body: FormData): Promise<DBCDiffResponse> =>
		apiCall(`/v3/dbcs/${encodeURIComponent(dbcID)}/diff`, {
			method: 'POST',
			data: body,
			timeout: TIMEOUT_NONE,
		}),
	createDBCParser: async (body: FormData): Promise<DBCResponse> =>
		apiCall('/v3/dbcs', {
			method: 'POST',
			data: body,
			timeout: TIMEOUT_NONE,
		}),
	updateDBCParsing: async (dbcID: string, body: FormData): Promise<SingleDBCResponse> =>
		apiCall(`/v3/dbcs/${encodeURIComponent(dbcID)}/update`, {
			method: 'PUT',
			data: body,
			timeout: TIMEOUT_NONE,
		}),
	updateDBCSettings: async (dbcID: string, body: EditDBCParserInput): Promise<DBCActionMessageResponse> =>
		apiCall(`/v3/dbcs/${encodeURIComponent(dbcID)}`, {
			method: 'PUT',
			data: body,
		}),
	deleteDBC: async (dbcID: string): Promise<DBCActionMessageResponse> =>
		apiCall(`/v3/dbcs/${encodeURIComponent(dbcID)}`, {
			method: 'DELETE',
		}),
	startDBCParser: async (dbcID: string): Promise<SingleDBCResponse> =>
		apiCall(`/v3/dbcs/${encodeURIComponent(dbcID)}/start`, {
			method: 'PUT',
		}),
	stopDBCParser: async (dbcID: string): Promise<SingleDBCResponse> =>
		apiCall(`/v3/dbcs/${encodeURIComponent(dbcID)}/stop`, {
			method: 'PUT',
		}),
	batchDeleteDBCs: async (body: BatchDBCRequest): Promise<DBCActionMessageResponse> =>
		apiCall('/v3/dbcs/batch/delete', {
			method: 'DELETE',
			data: body,
		}),
	batchStartDBCs: async (body: BatchDBCRequest): Promise<BatchActionResponse> =>
		apiCall('/v3/dbcs/batch/start', {
			method: 'PUT',
			data: body,
		}),
	batchStopDBCs: async (body: BatchDBCRequest): Promise<BatchActionResponse> =>
		apiCall('/v3/dbcs/batch/stop', {
			method: 'PUT',
			data: body,
		}),
}

export const dbcApi = isDemoDeployment() ? demoDbcApi : v3DbcApi
